import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { numbersApi } from '../../numbers.api';
import {
  ReadNumbersApiArg,
  ReadNumbersApiResponse,
} from '../../types/numbersApi.types';

import {
  createInitialState,
  readNumbersPaginatedReducer,
} from './useLazyReadNumbersPaginatedQuery.reducer';
import {
  ReadNumbersPaginatedChangeArgOptions,
  ReadNumbersPaginatedOptions,
  ReadNumbersPaginatedPublicArg,
  ReadNumbersPaginatedResult,
} from './useLazyReadNumbersPaginatedQuery.types';

/**
 * A numbers-domain pagination hook for the `readNumbers` endpoint.
 *
 * Owns cursor state internally, keeping it out of the public arg. Supports both
 * replace mode (table / prev-next pagination) and infinite mode (dropdown-style
 * append pagination).
 *
 * This is the lazy variant — call `changeArg` to trigger the first fetch.
 *
 * Why not use `useLazyPaginatedEndpoint`? The generic hook merges next/prev
 * page URLs back into its `arg` state (via `skipFetch: true`) so consumers can
 * read the current page params via the returned `debouncedArg`. That design
 * leaks opaque cursor tokens into what callers treat as a filter arg. This hook
 * keeps cursor URLs internal to avoid that coupling.
 *
 * @see {@link useReadNumbersPaginatedQuery} for the eager variant.
 */
export const useLazyReadNumbersPaginatedQuery = ({
  infinite = false,
}: ReadNumbersPaginatedOptions = {}): ReadNumbersPaginatedResult => {
  const dispatch = useAppDispatch();
  const { initiate } = numbersApi.endpoints.readNumbers;

  const [state, localDispatch] = useReducer(
    readNumbersPaginatedReducer,
    undefined,
    createInitialState,
  );

  const timeoutRef = useRef<number>();
  const debounceTimeRef = useRef(0);
  const argRef = useRef<ReadNumbersPaginatedPublicArg>();
  const [arg, setArg] = useState<ReadNumbersPaginatedPublicArg>();

  const mergePaginationPathWithArg = useCallback(
    (
      path: string,
      currentArg?: ReadNumbersPaginatedPublicArg,
    ): ReadNumbersPaginatedPublicArg | string => {
      if (!currentArg) {
        return path;
      }

      const url = new URL(path, 'https://dixa.invalid');
      const params = url.searchParams;

      if (!params.has('limit') && currentArg.limit !== undefined) {
        params.set('limit', String(currentArg.limit));
      }

      if (!params.has('search') && currentArg.searchTerm !== undefined) {
        params.set('search', currentArg.searchTerm);
      }

      if (!params.has('sms_capable') && currentArg.capability === 'sms') {
        params.set('sms_capable', 'true');
      }

      if (!params.has('pbx_region') && currentArg.pbxRegion !== undefined) {
        params.set('pbx_region', currentArg.pbxRegion);
      }

      const query = params.toString();
      return query.length > 0 ? `${url.pathname}?${query}` : url.pathname;
    },
    [],
  );

  const fetchResults = useCallback(
    async ({
      fetchArg,
      direction,
    }: {
      fetchArg: ReadNumbersPaginatedPublicArg | string;
      direction?: 'next' | 'previous';
    }) => {
      localDispatch({ type: 'fetchStarted', direction });

      const resolvedFetchArg =
        typeof fetchArg === 'string'
          ? mergePaginationPathWithArg(fetchArg, argRef.current)
          : fetchArg;

      const response = dispatch(
        initiate(resolvedFetchArg as unknown as ReadNumbersApiArg, {
          forceRefetch: true,
        }),
      );
      response.unsubscribe();

      const { data: responseData } = (await response) as {
        data?: ReadNumbersApiResponse;
      };

      if (responseData) {
        localDispatch({
          type: 'fetchSucceeded',
          items: responseData.data.numbers,
          nextPagePath: responseData.meta.next_page,
          previousPagePath: responseData.meta.prev_page,
          infinite,
          direction,
        });
      } else {
        localDispatch({ type: 'fetchFailed' });
      }
    },
    [dispatch, initiate, infinite, mergePaginationPathWithArg],
  );

  const changeArg = useCallback(
    (
      newArg: Partial<ReadNumbersPaginatedPublicArg>,
      options?: ReadNumbersPaginatedChangeArgOptions,
    ) => {
      debounceTimeRef.current = options?.debounceTime ?? 0;

      // Update argRef outside the setArg updater so it stays a pure setter and
      // safe under StrictMode/concurrent rendering.
      const merged = { ...(argRef.current ?? {}), ...newArg };
      argRef.current = merged;
      setArg(merged);

      // Invalidate cursor pagination synchronously so a `nextPage()` /
      // `previousPage()` call fired during the debounce window cannot reach
      // the API with a stale cursor paired with the new filters (e.g. clicking
      // "load more" right after switching region).
      localDispatch({ type: 'fetchStarted' });
    },
    [],
  );

  const nextPage = useCallback(async () => {
    if (!state.nextPagePath) {
      throw Error('Next page does not exist');
    }

    await fetchResults({
      fetchArg: state.nextPagePath,
      direction: 'next',
    });
  }, [fetchResults, state.nextPagePath]);

  const previousPage = useCallback(async () => {
    if (!state.previousPagePath) {
      throw Error('Previous page does not exist');
    }

    await fetchResults({
      fetchArg: state.previousPagePath,
      direction: 'previous',
    });
  }, [fetchResults, state.previousPagePath]);

  const refetchItems = useCallback(async () => {
    const currentArg = argRef.current;
    if (currentArg) {
      await fetchResults({ fetchArg: currentArg });
    }
  }, [fetchResults]);

  useEffect(() => {
    if (arg) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      const update = () => {
        fetchResults({ fetchArg: arg });
      };

      if (debounceTimeRef.current > 0) {
        timeoutRef.current = window.setTimeout(update, debounceTimeRef.current);
      } else {
        update();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arg]);

  return {
    items: state.items,
    asyncStatus: state.asyncStatus,
    hasNextPage: state.nextPagePath !== null,
    hasPreviousPage: state.previousPagePath !== null,
    nextPage,
    previousPage,
    changeArg,
    refetchItems,
  };
};
